// GS Mandal — NAAC Documents CMS (Institute portal).
// Only visible to non-school institutes (schools don't have NAAC accreditation).
// Fields: name, status (Draft/Published/Archived), order (1-100), objective, PDF (max 1 MB).

import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileText, Upload, Loader2, Download, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";

const inputCls = "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";
const STATUSES = ["Draft", "Published", "Archived"];

const emptyForm = () => ({
  name: "", objective: "", pdf_url: "", pdf_public_id: "", order: 1, status: "Published",
});

// Strip HTML tags for compact previews (the objective is stored as HTML by the RTE).
function stripHtml(html) {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export default function NaacDocuments() {
  const { role } = useAuth();
  const writable = role === "institute_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/naac-documents");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    // Default new-order = max(order)+1 within 1..100
    const nextOrder = Math.min(100, Math.max(1, (items.reduce((m, x) => Math.max(m, x.order || 0), 0) + 1)));
    setForm({ ...emptyForm(), order: nextOrder });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      objective: it.objective || "",
      pdf_url: it.pdf_url || "",
      pdf_public_id: it.pdf_public_id || "",
      order: it.order || 1,
      status: it.status || "Published",
    });
    setOpen(true);
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are allowed"); return; }
    if (file.size > 1 * 1024 * 1024) { toast.error("PDF must be 1 MB or smaller"); return; }
    const fd = new FormData();
    fd.append("kind", "naac_document");
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("pdf_url", data.url);
      set("pdf_public_id", data.public_id || "");
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Document Name is required");
    const order = parseInt(form.order, 10);
    if (isNaN(order) || order < 1 || order > 100) return toast.error("Order must be a whole number between 1 and 100");
    setSaving(true);
    try {
      const payload = { ...form, order };
      if (editing) {
        const { data } = await api.put(`/institute/naac-documents/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/institute/naac-documents", payload);
        setItems((arr) => [...arr, data].sort((a, b) => (a.order || 0) - (b.order || 0)));
        toast.success("Added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/naac-documents/${confirmDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="naac-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Accreditation</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17] flex items-center gap-3">
            <Award size={28} className="text-[#c78e42]" strokeWidth={1.5} /> NAAC Documents
          </h1>
          <p className="mt-2 text-sm text-[#6b655b]">
            Upload NAAC accreditation documents. Only documents with status <b>Published</b> appear on
            your institute&apos;s public website — the NAAC menu is hidden entirely when nothing is published.
          </p>
        </div>
        {writable ? (
          <button
            onClick={openCreate}
            data-testid="naac-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Document
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="naac-empty" className="border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center bg-white">
          <FileText size={28} className="mx-auto text-[#6b655b] mb-3" />
          <p className="text-sm text-[#6b655b]">No NAAC documents yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto" data-testid="naac-table">
          <table className="w-full text-sm">
            <thead className="bg-[#f9f8f6]">
              <tr>
                {["Order", "Name", "Status", "Objective", "Download", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-wide uppercase text-[#6b655b] font-semibold text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#efece6]">
              {items.map((it) => (
                <tr key={it.id} data-testid={`naac-row-${it.id}`} className="hover:bg-[#f9f8f6]/60">
                  <td className="px-4 py-3 font-mono text-[#1c1a17] w-20">{it.order}</td>
                  <td className="px-4 py-3 text-[#1c1a17] font-medium">{it.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
                      it.status === "Published"
                        ? "bg-[#234333]/10 text-[#234333] border-[#234333]/30"
                        : it.status === "Draft"
                          ? "bg-[#c78e42]/10 text-[#8a6d1d] border-[#c78e42]/30"
                          : "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/30"
                    }`}>{it.status}</span>
                  </td>
                  <td className="px-4 py-3 text-[#6b655b] max-w-md truncate" title={stripHtml(it.objective)}>{stripHtml(it.objective) || "—"}</td>
                  <td className="px-4 py-3">
                    {it.pdf_url ? (
                      <a href={it.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#234333] hover:text-[#c78e42] text-xs" data-testid={`naac-download-${it.id}`}>
                        <Download size={12} /> PDF
                      </a>
                    ) : <span className="text-[10px] text-[#8a8478]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {writable && (
                      <>
                        <button onClick={() => openEdit(it)} data-testid={`naac-edit-${it.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:text-[#c78e42] text-xs px-2 py-1"><Pencil size={12} /> Edit</button>
                        <button onClick={() => setConfirmDelete(it)} data-testid={`naac-delete-${it.id}`} className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 text-xs px-2 py-1 ml-1"><Trash2 size={12} /> Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>{editing ? "Edit NAAC Document" : "Add NAAC Document"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1" data-testid="naac-form-scroll">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">Document Name *</label>
              <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. SSR 2024" data-testid="naac-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">Order (1–100) *</label>
                <input type="number" min="1" max="100" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="naac-form-order" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">Status *</label>
                <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="naac-form-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">Objective of the Document</label>
              <div className="text-[11px] text-[#8a8478] mt-0.5 mb-1">Optional — you can format text, add lists, headings, links and text alignment.</div>
              <RichTextEditor
                testid="naac-form-objective"
                value={form.objective || ""}
                onChange={(html) => set("objective", html)}
                placeholder="Briefly explain what this document covers."
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-bold">PDF Attachment</label>
              <div className="mt-1 flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1b3326] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm cursor-pointer">
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploading ? "Uploading…" : (form.pdf_url ? "Replace PDF" : "Upload PDF")}
                  <input type="file" accept="application/pdf" hidden onChange={(e) => uploadPdf(e.target.files?.[0])} data-testid="naac-form-pdf" />
                </label>
                {form.pdf_url && (
                  <a href={form.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#234333] hover:text-[#c78e42] text-xs">
                    <FileText size={12} /> View uploaded PDF
                  </a>
                )}
              </div>
              <div className="text-[10px] text-[#8a8478] mt-1">PDF only · max 1 MB.</div>
            </div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-3 border-t border-[#efece6] shrink-0 bg-white">
            <button onClick={() => setOpen(false)} className="text-sm px-4 py-2 text-[#6b655b] hover:text-[#1c1a17]" data-testid="naac-form-cancel">Cancel</button>
            <button onClick={save} disabled={saving || uploading} data-testid="naac-form-save" className="text-sm bg-[#234333] hover:bg-[#1b3326] text-white px-5 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this NAAC document?</AlertDialogTitle>
            <AlertDialogDescription>The document and its PDF will be permanently removed and immediately hidden from your public website.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="naac-delete-confirm" className="bg-red-700 hover:bg-red-800">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
